import * as React from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { PageFilterBox } from "@/components/PageFilterBox";

export function Modal({
  open, onClose, title, children, footer, wide, isDirty, confirmMessage, stacked, onSave,
}: {
  open: boolean; onClose: () => void; title: string;
  children: React.ReactNode; footer?: React.ReactNode; wide?: boolean;
  // When true (or a function returning true), backdrop-click / the X button / Escape
  // will ask for confirmation before actually closing, so in-progress form
  // input isn't silently discarded.
  isDirty?: boolean | (() => boolean);
  confirmMessage?: string;
  // Render above another already-open modal (e.g. a "create new X" flow
  // launched from a dropdown inside a different modal).
  stacked?: boolean;
  onSave?: () => void;
}) {
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);

  const requestClose = React.useCallback(() => {
    const dirty = typeof isDirty === "function" ? isDirty() : isDirty;
    if (dirty && !window.confirm(confirmMessage || "Discard unsaved changes?")) return;
    onClose();
  }, [isDirty, confirmMessage, onClose]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const dialogs = document.querySelectorAll('[role="dialog"]');
        if (dialogs.length > 0 && dialogRef.current) {
          const topDialog = dialogs[dialogs.length - 1];
          if (topDialog !== dialogRef.current && !dialogRef.current.contains(topDialog)) {
            return;
          }
        }
        e.preventDefault();
        e.stopPropagation();
        requestClose();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        const dialogs = document.querySelectorAll('[role="dialog"]');
        if (dialogs.length > 0 && dialogRef.current) {
          const topDialog = dialogs[dialogs.length - 1];
          if (topDialog !== dialogRef.current && !dialogRef.current.contains(topDialog)) {
            return;
          }
        }
        e.preventDefault();
        e.stopPropagation();
        if (onSave) {
          onSave();
        } else if (dialogRef.current) {
          const btn = dialogRef.current.querySelector<HTMLButtonElement>(
            'div[data-modal-footer] button:last-child, button[type="submit"]'
          );
          btn?.click();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, requestClose, onSave]);

  if (!open) return null;
  return (
    <div className={cn("fixed inset-0 flex items-start md:items-center justify-center p-4 bg-black/60 overflow-y-auto", stacked ? "z-[60]" : "z-50")} onClick={requestClose}>
      <div
        ref={dialogRef}
        role="dialog" aria-modal="true"
        className={cn("bg-card border border-border rounded-xl w-full my-8 max-h-[90vh] overflow-y-auto p-5", wide ? "max-w-3xl" : "max-w-lg")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 mb-4">
          <h3 className="text-base font-semibold">{title}</h3>
          {/* Every modal gets the page filter too, scoped to the modal body;
              it always highlights (a modal is never a list with a filter
              contract), and `/` reaches it because it renders above the
              page's box. */}
          <div className="flex items-center gap-2">
            <PageFilterBox scopeRef={bodyRef} />
            <button onClick={requestClose} className="h-7 w-7 grid place-items-center rounded hover:bg-secondary cursor-pointer" aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>
        <div ref={bodyRef}>{children}</div>
        {footer && (
          <div data-modal-footer className="flex items-center justify-between gap-2 mt-5 pt-3 border-t border-border/40">
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1 select-none">
              <kbd className="px-1 py-0.2 bg-secondary border border-border rounded text-[10px] font-mono">⌘↵</kbd>
              <span>save</span>
              <span className="mx-1 opacity-40">·</span>
              <kbd className="px-1 py-0.2 bg-secondary border border-border rounded text-[10px] font-mono">esc</kbd>
              <span>cancel</span>
            </span>
            <div className="flex justify-end gap-2">{footer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
